import { GUIDE_Z_INDEX } from './constants';

/**
 * CSS inyectado en cada shadow DOM. Como `:host { all: initial }` rompe
 * con la herencia del host, todos los estilos viven aquí. Las variables
 * CSS (`--veo-*`) son los puntos de extensión para tematización futura.
 */
export const GUIDE_STYLES = `
:host {
  --veo-primary: #FF5B35;
  --veo-text: #1f2937;
  --veo-text-secondary: #4b5563;
  --veo-bg: #ffffff;
  --veo-radius: 12px;
  --veo-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  --veo-width: 420px;
  --veo-actions-justify: flex-end;
  all: initial;
}
* { box-sizing: border-box; font-family: var(--veo-font, -apple-system, system-ui, "Segoe UI", Roboto, sans-serif); }

.veo-modal-overlay {
  position: fixed; inset: 0;
  background: rgba(15, 15, 30, 0.45);
  z-index: ${GUIDE_Z_INDEX};
  animation: veo-fade-in 180ms ease-out;
}
/* Sin backdrop (style.backdrop === false): la app queda usable detrás; solo la
   tarjeta captura el mouse. El click-en-backdrop deja de cerrar (no hay backdrop). */
.veo-modal-overlay--none {
  background: transparent;
  pointer-events: none;
}
.veo-modal-overlay--none .veo-modal-card { pointer-events: auto; }
/*
 * Posición libre/preset: --veo-pos-x/y son porcentajes (default 50% = centro).
 * El truco translate(-pos) alinea la MISMA fracción de la tarjeta con esa
 * fracción del overlay, así la tarjeta nunca se sale (0% = pegada izquierda,
 * 100% = pegada derecha, 50% = centrada) sea cual sea su ancho.
 */
.veo-modal-card {
  position: absolute;
  left: var(--veo-pos-x, 50%);
  top: var(--veo-pos-y, 50%);
  transform: translate(calc(var(--veo-pos-x, 50%) * -1), calc(var(--veo-pos-y, 50%) * -1));
  background: var(--veo-bg);
  border-radius: var(--veo-radius);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  padding: var(--veo-pad, 24px);
  max-width: var(--veo-width); width: 90%;
  min-height: var(--veo-min-h, auto);
  box-shadow: var(--veo-shadow);
  animation: veo-fade-in 180ms ease-out;
}

.veo-banner {
  position: fixed; left: 0; right: 0;
  background: var(--veo-bg);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  padding: var(--veo-pad, 16px);
  z-index: ${GUIDE_Z_INDEX};
  box-shadow: var(--veo-shadow, 0 2px 12px rgba(0, 0, 0, 0.12));
  animation: veo-slide-down 200ms ease-out;
}
.veo-banner-top { top: 0; }
.veo-banner-bottom { bottom: 0; }
/* Banner EMBEBIDO en un contenedor (step.selector): fluye dentro del contenedor
   y empuja su contenido, en vez de flotar fijo sobre la pantalla. */
.veo-banner-embedded {
  position: static;
  left: auto; right: auto;
  width: 100%;
  border-radius: var(--veo-radius, 0);
}

.veo-tooltip {
  position: absolute;
  background: var(--veo-bg);
  border-radius: var(--veo-radius);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  padding: var(--veo-pad, 15px);
  max-width: 300px;
  z-index: ${GUIDE_Z_INDEX};
  box-shadow: var(--veo-shadow, 0 8px 30px rgba(0, 0, 0, 0.18));
  animation: veo-fade-in 150ms ease-out;
}
.veo-tooltip-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--veo-bg);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  transform: rotate(45deg);
  pointer-events: none;
}

.veo-inline {
  background: var(--veo-bg);
  border-radius: var(--veo-radius);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  padding: var(--veo-pad, 20px);
  max-width: var(--veo-width);
  margin: var(--veo-margin, 12px) 0;
  position: relative;
  box-shadow: var(--veo-shadow);
  animation: veo-fade-in 160ms ease-out;
}

/* ── Modelo unificado (composite) ── */
.veo-composite-overlay {
  position: fixed; inset: 0;
  z-index: ${GUIDE_Z_INDEX};
  pointer-events: none;
}
/* Backdrop atenuado (cualquier posición flotante con backdrop activo). */
.veo-composite-overlay--dim {
  background: rgba(0, 0, 0, 0.5);
  pointer-events: auto;
}
.veo-composite-anchored {
  position: fixed;
  z-index: ${GUIDE_Z_INDEX};
}
/* Card genérica del modelo unificado. El diseño (bg/borde/padding/…) lo aplica
   el renderer como estilos inline desde step.design; acá van los defaults. */
.veo-card {
  pointer-events: auto;
  background: var(--veo-bg, #ffffff);
  color: var(--veo-text, #1f2937);
  border-radius: var(--veo-radius, 12px);
  padding: 20px;
  max-width: 90vw;
  box-shadow: var(--veo-shadow, 0 12px 40px rgba(0, 0, 0, 0.18));
  animation: veo-fade-in 160ms ease-out;
}
.veo-composite-anchored .veo-card, .veo-composite-overlay .veo-card { position: relative; }

.veo-guide-content {
  position: relative;
  display: flex; flex-direction: column;
}
/* Reserva espacio para la ✕ (esquina sup. derecha) SOLO en el primer bloque:
   así el texto alineado a la derecha no queda tapado por el botón de cerrar. */
.veo-guide-content > .veo-guide-title:first-child,
.veo-guide-content > .veo-guide-text:first-child {
  padding-right: 20px;
}
.veo-guide-image {
  display: block;
  width: var(--veo-image-width, 100%);
  max-height: 180px;
  object-fit: cover;
  align-self: var(--veo-image-align, stretch);
  border-radius: var(--veo-image-radius, 8px);
  margin: var(--veo-image-mt, 0) 0 var(--veo-image-mb, 12px);
}
/* Imagen A SANGRE: rompe el padding de la tarjeta y ocupa el ancho completo
   (hero estilo anuncio). Como primer bloque, hereda el redondeo superior. */
.veo-guide-image--bleed {
  width: calc(100% + var(--veo-pad, 24px) * 2);
  max-width: none;
  max-height: 280px;
  align-self: auto;
  border-radius: 0;
  margin: 0 calc(var(--veo-pad, 24px) * -1) 12px;
}
.veo-guide-content > .veo-guide-image--bleed:first-child {
  margin-top: calc(var(--veo-pad, 24px) * -1);
  border-radius: calc(var(--veo-radius, 12px) - var(--veo-border-width, 0px))
    calc(var(--veo-radius, 12px) - var(--veo-border-width, 0px)) 0 0;
}
.veo-guide-title {
  font-size: var(--veo-title-size, 18px); font-weight: 600; line-height: 1.3;
  text-align: var(--veo-title-align, left);
  margin: var(--veo-title-mt, 0) 0 var(--veo-title-mb, 6px);
  color: var(--veo-title-color, var(--veo-text));
}
.veo-guide-text {
  font-size: var(--veo-text-size, 14px); line-height: 1.5;
  text-align: var(--veo-text-align, left);
  margin: var(--veo-text-mt, 0) 0 var(--veo-text-mb, 16px);
  color: var(--veo-text-color, var(--veo-text-secondary));
}
/* Rich text dentro de un bloque de texto (p/listas/links/énfasis). */
.veo-guide-text p { margin: 0 0 8px; }
.veo-guide-text p:last-child { margin-bottom: 0; }
.veo-guide-text ul, .veo-guide-text ol { margin: 0 0 8px; padding-left: 20px; }
.veo-guide-text ul { list-style: disc; }
.veo-guide-text ol { list-style: decimal; }
.veo-guide-text li { margin: 2px 0; display: list-item; }
.veo-guide-text a { color: var(--veo-primary); text-decoration: underline; cursor: pointer; }
.veo-guide-text strong { font-weight: 600; }
.veo-guide-text em { font-style: italic; }
.veo-guide-text u { text-decoration: underline; }
.veo-guide-text s { text-decoration: line-through; }
/*
 * El ÚLTIMO bloque de contenido no deja su margin-bottom colgando: ese margen
 * existe para separar del bloque SIGUIENTE, y sin siguiente se suma al padding
 * de la tarjeta y parece un "hueco fantasma" (p.ej. quitar el botón de un
 * tooltip dejaba el espacio como si siguiera ahí). El penúltimo hijo es el
 * último bloque real (el ✕ de cerrar siempre va último y es absoluto). La
 * mayor especificidad le gana a las reglas por clase, pero NO a un margen
 * inline elegido explícitamente por el autor (block.style.marginBottom).
 */
.veo-guide-content > :nth-last-child(2) {
  margin-bottom: 0;
}
.veo-guide-actions {
  display: flex; gap: 8px; justify-content: var(--veo-actions-justify);
}
.veo-guide-cta {
  background: var(--veo-cta-bg, var(--veo-primary));
  color: var(--veo-cta-color, #fff); border: none;
  padding: 9px 18px; border-radius: var(--veo-cta-radius, 8px);
  font-size: var(--veo-cta-size, 14px); font-weight: 500; cursor: pointer;
  transition: opacity 120ms ease;
}
.veo-guide-cta:hover { opacity: 0.9; }
.veo-guide-cta:active { transform: translateY(1px); }
.veo-guide-close {
  position: absolute; top: -6px; right: -6px;
  width: 24px; height: 24px;
  background: none; border: none;
  font-size: 22px; line-height: 1;
  cursor: pointer; color: #9ca3af; padding: 0;
}
.veo-guide-close:hover { color: var(--veo-text); }
/*
 * Si el PRIMER bloque es una imagen, la ✕ quedaría sobre la esquina de la foto
 * (invisible según el color). En ese caso pasa a ser una pastilla flotante con
 * fondo propio, legible sobre cualquier imagen. (Sin soporte de :has() queda el
 * estilo base — mismo comportamiento de antes, sin regresión.)
 */
.veo-guide-content:has(> .veo-guide-image:first-child) > .veo-guide-close {
  top: 2px; right: 2px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; color: #374151;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
}
.veo-guide-content:has(> .veo-guide-image:first-child) > .veo-guide-close:hover {
  color: #111827;
}

.veo-form { display: flex; flex-direction: column; gap: 14px; margin-bottom: 16px; }
.veo-form-field { display: flex; flex-direction: column; gap: 6px; }
.veo-form-label { font-size: 13px; font-weight: 500; color: var(--veo-text); }
.veo-form-input {
  font-size: 14px; color: var(--veo-text);
  background: var(--veo-bg);
  border: 1px solid #d1d5db; border-radius: 8px;
  padding: 8px 10px; width: 100%;
}
.veo-form-input:focus { outline: 2px solid var(--veo-primary); outline-offset: -1px; border-color: transparent; }
.veo-form-options { display: flex; flex-direction: column; gap: 6px; }
.veo-form-option {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; color: var(--veo-text); cursor: pointer;
}
.veo-form-option input { accent-color: var(--veo-primary); margin: 0; cursor: pointer; }
.veo-form-scale { display: flex; gap: 4px; flex-wrap: wrap; }
.veo-nps-btn {
  min-width: 30px; height: 30px; padding: 0 4px;
  font-size: 13px; font-weight: 500; color: var(--veo-text);
  background: var(--veo-bg); border: 1px solid #d1d5db; border-radius: 6px;
  cursor: pointer; transition: all 100ms ease;
}
.veo-nps-btn:hover { border-color: var(--veo-primary); }
.veo-rating-btn {
  background: none; border: none; padding: 0 2px;
  font-size: 26px; line-height: 1; color: #d1d5db;
  cursor: pointer; transition: color 100ms ease;
}
.veo-scale-active.veo-nps-btn { background: var(--veo-primary); color: #fff; border-color: var(--veo-primary); }
.veo-scale-active.veo-rating-btn { color: #f59e0b; }
.veo-form-error { font-size: 13px; color: #dc2626; margin: 0; }
.veo-form-thanks {
  text-align: center; padding: 24px 8px;
  font-size: 16px; font-weight: 600; color: var(--veo-text);
}
.veo-form-preview-note {
  text-align: center; margin: 0 0 12px;
  font-size: 12px; color: #b45309;
}

.veo-walkthrough {
  position: absolute;
  background: var(--veo-bg);
  border-radius: var(--veo-radius);
  border: var(--veo-border-width, 0) solid var(--veo-border-color, transparent);
  padding: var(--veo-pad, 16px);
  max-width: 340px;
  z-index: ${GUIDE_Z_INDEX};
  box-shadow: var(--veo-shadow, 0 8px 30px rgba(0, 0, 0, 0.2));
  animation: veo-fade-in 150ms ease-out;
}
.veo-walkthrough-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--veo-bg);
  transform: rotate(45deg);
  pointer-events: none;
}
.veo-walkthrough-counter {
  font-size: 11px; color: #9ca3af;
  margin-bottom: 8px; letter-spacing: 0.02em;
}
.veo-walkthrough-progress {
  display: flex; gap: 4px;
  margin-bottom: 12px;
}
.veo-walkthrough-progress-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #e5e7eb;
  transition: background 200ms ease;
}
.veo-walkthrough-progress-dot.active { background: var(--veo-primary); }
.veo-walkthrough-progress-dot.completed { background: var(--veo-primary); opacity: 0.5; }
.veo-walkthrough-actions {
  display: flex; align-items: center;
  margin-top: 16px; gap: 8px;
}
.veo-walkthrough-actions-right {
  display: flex; gap: 8px;
  margin-left: auto;
}
.veo-walkthrough-btn-secondary {
  background: transparent; color: #6b7280;
  border: 1px solid #e5e7eb;
  padding: 8px 16px; border-radius: 8px;
  font-size: 13px; cursor: pointer;
  transition: background 120ms ease;
}
.veo-walkthrough-btn-secondary:hover { background: #f9fafb; }
.veo-walkthrough-skip {
  background: transparent; color: #9ca3af;
  border: none; padding: 4px 8px;
  font-size: 12px; cursor: pointer;
}
.veo-walkthrough-skip:hover { color: var(--veo-text); }

/* ── Badge (elemento inyectado junto al ancla que abre un tooltip) ── */
.veo-badge {
  display: inline-flex; align-items: center; justify-content: center;
  border: none; padding: 0; margin: 0 4px;
  background: none; cursor: pointer;
  line-height: 1; vertical-align: middle;
  font-family: inherit;
}
.veo-badge:focus-visible { outline: 2px solid var(--veo-primary); outline-offset: 2px; }
.veo-badge--icon {
  border-radius: 50%;
  background: color-mix(in srgb, var(--veo-primary) 14%, transparent);
  color: var(--veo-primary);
  font-weight: 600;
}
.veo-badge--dot {
  border-radius: 50%;
  background: var(--veo-primary);
  animation: veo-badge-pulse 2s ease-out infinite;
}
.veo-badge--pill {
  border-radius: 999px;
  background: var(--veo-primary);
  color: #fff;
  font-weight: 600;
  padding: 3px 9px;
  white-space: nowrap;
}
.veo-badge--text {
  color: var(--veo-primary);
  font-weight: 600;
  white-space: nowrap;
  text-decoration: underline dotted;
  text-underline-offset: 3px;
}
.veo-badge--image img { display: block; border-radius: 4px; object-fit: cover; }
/* El tooltip del badge usa strategy fixed (el host vive dentro del flujo del
   cliente; un absoluto se recortaría con overflow de ancestros). Su ancho es
   personalizable vía var dedicada (--veo-width no sirve: el :host la define
   en 420px siempre y pisaría el default de 300px de los tooltips). */
.veo-badge-tooltip {
  position: fixed;
  max-width: var(--veo-badge-tip-width, 300px);
}
@keyframes veo-badge-pulse {
  0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--veo-primary) 45%, transparent); }
  70% { box-shadow: 0 0 0 7px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}

.veo-custom-floating {
  position: fixed;
  z-index: ${GUIDE_Z_INDEX};
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  animation: veo-fade-in 160ms ease;
}
.veo-custom-anchored {
  position: fixed;
  top: 0; left: 0;
  z-index: ${GUIDE_Z_INDEX};
  max-width: calc(100vw - 16px);
  animation: veo-fade-in 160ms ease;
}
.veo-custom-inline {
  display: block;
  position: relative;
  margin: 12px 0;
}
.veo-custom-frame {
  display: block;
  border: 0;
  width: 360px;
  background: transparent;
  color-scheme: normal;
}

@keyframes veo-fade-in { from { opacity: 0; } to { opacity: 1; } }
@keyframes veo-slide-up {
  from { transform: translateY(10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
@keyframes veo-slide-down {
  from { transform: translateY(-10px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}
`;
